import React, { useState, useMemo } from "react";
import { Row, Col, Card, Form, Spinner } from "react-bootstrap";
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, ArcElement, Title, Tooltip, Legend,
} from "chart.js";
import { Line, Bar, Doughnut } from "react-chartjs-2";
import { useStatsTickets }       from "../../hooks/STATISTIQUES/useStatsTickets";
import { useStatsInterventions } from "../../hooks/STATISTIQUES/useStatsInterventions";
import { useStatsProjets }       from "../../hooks/STATISTIQUES/useStatsProjets";

// synthèse = tickets + interventions + projets agrégés → on appelle les 3 dédiés directement

ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, ArcElement, Title, Tooltip, Legend
);

// ── Constantes ────────────────────────────────────────────────────────────────

const PERIOD_OPTIONS = [
  { value: "mois",      label: "Ce mois" },
  { value: "mois-1",   label: "Mois dernier" },
  { value: "semaine",   label: "Cette semaine" },
  { value: "semaine-1", label: "Semaine dernière" },
  { value: "7j",        label: "7 derniers jours" },
  { value: "30j",       label: "30 derniers jours" },
  { value: "90j",       label: "90 derniers jours" },
  { value: "annee",     label: "Cette année" },
  { value: "annee-1",   label: "L'année dernière" },
  { value: "jour",      label: "Aujourd'hui" },
  { value: "custom",    label: "Personnalisé" },
];

const STATUS_COLORS = {
  open:        "#4e73df",
  in_progress: "#f6c23e",
  closed:      "#36b9cc",
  approved:    "#1cc88a",
};

const PRIORITY_COLORS = {
  secondary: "#858796",
  normal:    "#4e73df",
  high:      "#f6c23e",
  urgent:    "#e74a3b",
};

const PALETTE = ["#4e73df","#1cc88a","#36b9cc","#f6c23e","#e74a3b","#858796","#5a5c69","#6610f2"];

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmtDuration = (min) => {
  if (min == null || isNaN(min)) return "—";
  if (min === 0) return "0h";
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m > 0 ? `${h}h${String(m).padStart(2, "0")}` : `${h}h`;
};

const fmtEvolDate = (d) => {
  if (!d) return "";
  if (d.length === 7) return new Date(d + "-01").toLocaleDateString("fr-FR", { month: "short", year: "2-digit" });
  return new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" });
};

const truncate = (str, n = 30) => (str?.length ?? 0) > n ? str.slice(0, n) + "…" : (str || "");

const fmtTypeName = (nom) =>
  (nom || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

// ── Hooks utilitaires ─────────────────────────────────────────────────────────

function useChartTheme() {
  const isDark = document.documentElement.getAttribute("data-bs-theme") === "dark";
  return {
    textColor: isDark ? "rgba(255,255,255,0.75)" : "rgba(50,50,50,0.8)",
    gridColor: isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.06)",
    tooltipBg: isDark ? "#353535" : "#fff",
    tooltipFg: isDark ? "#fff" : "#353535",
  };
}

// ── Composants locaux ─────────────────────────────────────────────────────────

function KpiCard({ label, value, sub, color = "primary" }) {
  return (
    <Card className={`border-left-${color} shadow h-100 py-2`}>
      <Card.Body>
        <div className="row no-gutters align-items-center">
          <div className="col mr-2">
            <div className={`text-xs font-weight-bold text-${color} text-uppercase mb-1`} style={{ fontSize: "0.7rem" }}>
              {label}
            </div>
            <div className="h5 mb-0 font-weight-bold text-gray-800">{value ?? "—"}</div>
            {sub && <div className="text-xs text-muted mt-1" style={{ fontSize: "0.72rem" }}>{sub}</div>}
          </div>
        </div>
      </Card.Body>
    </Card>
  );
}

function ChartCard({ title, height = 220, children }) {
  return (
    <Card className="shadow-sm h-100">
      <Card.Header className="py-2 px-3">
        <span className="small fw-semibold">{title}</span>
      </Card.Header>
      <Card.Body style={{ height }}>{children}</Card.Body>
    </Card>
  );
}

// ── Composant principal ───────────────────────────────────────────────────────

const StatsPanel = ({ compact = false, clientId = null }) => {
  const [periode, setPeriode]     = useState("mois");
  const [dateDebut, setDateDebut] = useState("");
  const [dateFin, setDateFin]     = useState("");

  const filters = useMemo(() => {
    const f = { periode };
    if (periode === "custom") {
      if (dateDebut) f.date_debut = dateDebut;
      if (dateFin)   f.date_fin   = dateFin;
    }
    if (clientId) f.client_id = clientId;
    return f;
  }, [periode, dateDebut, dateFin, clientId]);

  // /statistique/tickets  → tik.{ total, par_statut, par_type, par_priorite, par_client,
  //                               par_utilisateur, evolution, interventions.{par_utilisateur,par_ticket} }
  // /statistique/interventions → interv.{ total, tickets.{duree_totale,nombre,par_utilisateur,par_ticket},
  //                                        projets.{duree_totale,nombre,par_utilisateur,par_projet} }
  // /statistique/projets  → proj.{ total, par_statut, par_client, par_utilisateur,
  //                                interventions.{duree_totale,nombre,par_utilisateur,par_projet} }
  // Note: par_client = null (pas []) quand client_id est passé

  const { data: rawTik,    isLoading: lTik    } = useStatsTickets(filters);
  const { data: rawInterv, isLoading: lInterv  } = useStatsInterventions(filters);
  const { data: rawProj,   isLoading: lProj    } = useStatsProjets(filters);

  const isLoading = lTik || lInterv || lProj;

  const tik    = rawTik?.data;
  const interv = rawInterv?.data;
  const proj   = rawProj?.data;

  // Données réelles présentes = au moins un ticket ou une minute d'intervention
  const hasData = (tik?.total ?? 0) > 0 || (interv?.total?.duree_totale ?? 0) > 0 || (proj?.total ?? 0) > 0;

  const { textColor, gridColor, tooltipBg, tooltipFg } = useChartTheme();

  const baseOpts = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { labels: { color: textColor, boxWidth: 12, font: { size: 11 } } },
      tooltip: { backgroundColor: tooltipBg, titleColor: tooltipFg, bodyColor: tooltipFg, borderColor: gridColor, borderWidth: 1 },
    },
    scales: {
      x: { ticks: { color: textColor, font: { size: 10 } }, grid: { color: gridColor } },
      y: { ticks: { color: textColor, font: { size: 10 } }, grid: { color: gridColor } },
    },
  };
  const noScales = { ...baseOpts, scales: undefined };
  const hBar     = { ...baseOpts, indexAxis: "y", plugins: { ...baseOpts.plugins, legend: { display: false } } };


  // Tooltip affichant la durée formatée (axe=heures → on convertit en minutes pour fmtDuration)
  const hBarDur = {
    ...hBar,
    plugins: {
      ...hBar.plugins,
      tooltip: {
        ...hBar.plugins.tooltip,
        callbacks: { label: (ctx) => ` ${fmtDuration(Math.round(ctx.raw * 60))}` },
      },
    },
  };

  // ── KPI ───────────────────────────────────────────────────────────
  const totalTickets        = tik?.total ?? 0;
  const intervTicketsDuree  = interv?.tickets?.duree_totale ?? 0;
  const intervTicketsNombre = interv?.tickets?.nombre ?? 0;
  const intervProjetsDuree  = interv?.projets?.duree_totale ?? 0;
  const intervProjetsNombre = interv?.projets?.nombre ?? 0;
  const totalProjets        = proj?.total ?? 0;

  const closedCount  = (tik?.par_statut?.find(s => s.statut === "closed")?.total  ?? 0)
                     + (tik?.par_statut?.find(s => s.statut === "approved")?.total ?? 0);
  const tauxCloture  = totalTickets > 0 ? `${Math.round(closedCount / totalTickets * 100)}%` : "—";
  const openCount    = tik?.par_statut?.find(s => s.statut === "open")?.total ?? 0;
  const inProgCount  = proj?.par_statut?.find(s => s.statut === "in_progress")?.total ?? 0;

  // Période affichée
  const periodeDebut = tik?.periode?.debut;
  const periodeFin   = tik?.periode?.fin;
  const periodeRange = periodeDebut && periodeFin
    ? `${new Date(periodeDebut).toLocaleDateString("fr-FR")} – ${new Date(periodeFin).toLocaleDateString("fr-FR")}`
    : null;

  // ── Évolution (tik.evolution) ─────────────────────────────────────
  // On filtre les périodes où il y a vraiment eu de l'activité
  const evolRaw    = tik?.evolution || [];
  const evolHasData = evolRaw.some(e => e.crees > 0 || e.fermes > 0);
  const evolLabels = evolRaw.map(e => fmtEvolDate(e.date));
  const evolData   = {
    labels: evolLabels,
    datasets: [
      { label: "Créés",  data: evolRaw.map(e => e.crees),  borderColor: "#4e73df", backgroundColor: "rgba(78,115,223,0.15)", tension: 0.3, fill: true, pointRadius: 2 },
      { label: "Fermés", data: evolRaw.map(e => e.fermes), borderColor: "#1cc88a", backgroundColor: "rgba(28,200,138,0.12)", tension: 0.3, fill: true, pointRadius: 2 },
    ],
  };

  // ── Par statut (tik.par_statut) ───────────────────────────────────
  const parStatut  = tik?.par_statut || [];
  const statutData = {
    labels:   parStatut.map(s => s.label),
    datasets: [{
      data:            parStatut.map(s => s.total),
      backgroundColor: parStatut.map(s => STATUS_COLORS[s.statut] || "#858796"),
      borderWidth: 2,
    }],
  };

  // ── Par type (tik.par_type) ───────────────────────────────────────
  const parType  = tik?.par_type || [];
  const typeData = {
    labels:   parType.map(t => fmtTypeName(t.nom)),
    datasets: [{
      label: "Tickets",
      data:  parType.map(t => t.total),
      backgroundColor: parType.map((_, i) => PALETTE[i % PALETTE.length]),
      borderWidth: 2,
    }],
  };

  // ── Par priorité (tik.par_priorite) ──────────────────────────────
  const parPriorite  = tik?.par_priorite || [];
  const prioriteData = {
    labels:   parPriorite.map(p => fmtTypeName(p.nom)),
    datasets: [{
      data:            parPriorite.map(p => p.total),
      backgroundColor: parPriorite.map(p => PRIORITY_COLORS[p.nom] || "#858796"),
      borderWidth: 2,
    }],
  };

  // ── Interventions par utilisateur (interv.tickets.par_utilisateur) ─
  const intervParUser  = interv?.tickets?.par_utilisateur || [];
  const intervUserData = {
    labels:   intervParUser.map(u => `${u.prenom} ${u.nom}`),
    datasets: [{
      label: "Heures",
      data:  intervParUser.map(u => +((u.duree_totale / 60).toFixed(1))),
      backgroundColor: intervParUser.map((_, i) => PALETTE[i % PALETTE.length]),
    }],
  };
  const intervUserHeight = Math.max(220, intervParUser.length * 36);

  // ── Top tickets par durée (interv.tickets.par_ticket) ────────────
  // L'API peut retourner 1000+ tickets — on limite à 10
  const intervParTicket  = interv?.tickets?.par_ticket || [];
  const topTicketsData   = {
    labels:   intervParTicket.slice(0, 10).map(t => truncate(t.titre)),
    datasets: [{
      label: "Heures",
      data:  intervParTicket.slice(0, 10).map(t => +((t.duree_totale / 60).toFixed(1))),
      backgroundColor: PALETTE[0],
    }],
  };

  // ── Top projets par durée (proj.interventions.par_projet) ────────
  const projParProjet  = proj?.interventions?.par_projet || interv?.projets?.par_projet || [];
  const topProjetsData = {
    labels:   projParProjet.slice(0, 8).map(p => truncate(p.titre)),
    datasets: [{
      label: "Heures",
      data:  projParProjet.slice(0, 8).map(p => +((p.duree_totale / 60).toFixed(1))),
      backgroundColor: PALETTE[2],
    }],
  };

  // ── Page complète — par client (tickets + heures interventions) ───
  // tik.par_client = null quand client_id est filtré
  const tikParClient       = tik?.par_client || [];
  const intervParClient    = interv?.tickets?.par_client || [];
  const tikParClientData   = {
    labels:   tikParClient.slice(0, 10).map(c => truncate(c.nom, 32)),
    datasets: [{ label: "Tickets", data: tikParClient.slice(0, 10).map(c => c.total), backgroundColor: PALETTE[0] }],
  };
  const intervClientData   = {
    labels:   intervParClient.slice(0, 10).map(c => truncate(c.nom, 32)),
    datasets: [{
      label: "Heures",
      data:  intervParClient.slice(0, 10).map(c => +((c.duree_totale / 60).toFixed(1))),
      backgroundColor: PALETTE[1],
    }],
  };

  // ── Page complète — par utilisateur (tickets) ────────────────────
  const tikParUser     = tik?.par_utilisateur || [];
  const userTicketData = {
    labels:   tikParUser.map(u => `${u.prenom ?? ""} ${u.nom ?? ""}`.trim()),
    datasets: [{ label: "Tickets", data: tikParUser.map(u => u.total), backgroundColor: PALETTE[2] }],
  };
  const tikUserHeight = Math.max(220, tikParUser.length * 36);

  // ── Rendu ─────────────────────────────────────────────────────────

  return (
    <div>
      {/* Sélecteur de période */}
      <Row className="mb-3 align-items-end">
        <Col xs={12} md={compact ? 12 : 5} lg={compact ? 10 : 4}>
          <div className="d-flex flex-wrap gap-2 align-items-end">
            <Form.Select size="sm" value={periode} onChange={e => setPeriode(e.target.value)} style={{ maxWidth: 200 }}>
              {PERIOD_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </Form.Select>
            {periode === "custom" && (
              <>
                <Form.Control size="sm" type="date" value={dateDebut} onChange={e => setDateDebut(e.target.value)} style={{ maxWidth: 150 }} />
                <span className="text-muted small">→</span>
                <Form.Control size="sm" type="date" value={dateFin}   onChange={e => setDateFin(e.target.value)}   style={{ maxWidth: 150 }} />
              </>
            )}
          </div>
          {periodeRange && <div className="text-muted mt-1" style={{ fontSize: "0.72rem" }}>{periodeRange}</div>}
        </Col>
      </Row>

      {isLoading && (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <div className="text-muted mt-2 small">Chargement des statistiques…</div>
        </div>
      )}

      {!isLoading && !hasData && (
        <div className="text-center text-muted py-5">
          <div style={{ fontSize: "2rem", opacity: 0.3 }}>📊</div>
          <div className="mt-2">Aucune donnée disponible pour cette période.</div>
        </div>
      )}

      {!isLoading && hasData && (
        <>
          {/* ── KPI ──────────────────────────────────────────────────── */}
          <Row className="mb-4 g-3">
            <Col xs={6} md={compact ? 6 : 3}>
              <KpiCard label="Tickets" value={totalTickets} sub={`${openCount} ouvert${openCount > 1 ? "s" : ""}`} color="warning" />
            </Col>
            <Col xs={6} md={compact ? 6 : 3}>
              <KpiCard
                label="Interventions tickets"
                value={fmtDuration(intervTicketsDuree)}
                sub={`${intervTicketsNombre} intervention${intervTicketsNombre > 1 ? "s" : ""}`}
                color="info"
              />
            </Col>
            <Col xs={6} md={compact ? 6 : 3}>
              <KpiCard
                label="Interventions projets"
                value={fmtDuration(intervProjetsDuree)}
                sub={`${intervProjetsNombre} intervention${intervProjetsNombre > 1 ? "s" : ""}`}
                color="primary"
              />
            </Col>
            <Col xs={6} md={compact ? 6 : 3}>
              {compact ? (
                <KpiCard label="Projets" value={totalProjets} sub={`${inProgCount} en cours`} color="success" />
              ) : (
                <KpiCard label="Taux de clôture" value={tauxCloture} color="success" />
              )}
            </Col>
          </Row>

          {/* ── Évolution + Statut ────────────────────────────────────── */}
          <Row className="mb-4 g-3">
            {evolHasData && (
              <Col xs={12} md={8}>
                <ChartCard title="Évolution des tickets" height={compact ? 180 : 220}>
                  <Line data={evolData} options={baseOpts} />
                </ChartCard>
              </Col>
            )}
            {parStatut.length > 0 && (
              <Col xs={12} md={evolHasData ? 4 : 6}>
                <ChartCard title="Répartition par statut" height={compact ? 180 : 220}>
                  <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Doughnut
                      data={statutData}
                      options={{ ...noScales, plugins: { ...noScales.plugins, legend: { position: "bottom", labels: { color: textColor, boxWidth: 10, font: { size: 10 } } } } }}
                    />
                  </div>
                </ChartCard>
              </Col>
            )}
            {parPriorite.length > 0 && !evolHasData && (
              <Col xs={12} md={6}>
                <ChartCard title="Répartition par priorité" height={compact ? 180 : 220}>
                  <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Doughnut
                      data={prioriteData}
                      options={{ ...noScales, plugins: { ...noScales.plugins, legend: { position: "bottom", labels: { color: textColor, boxWidth: 10, font: { size: 10 } } } } }}
                    />
                  </div>
                </ChartCard>
              </Col>
            )}
          </Row>

          {/* ── Par type de ticket ────────────────────────────────────── */}
          {parType.length > 0 && (
            <Row className="mb-4 g-3">
              <Col xs={12} md={parPriorite.length > 0 && evolHasData ? 8 : 12}>
                <ChartCard title="Tickets par type">
                  <Bar data={typeData} options={hBar} />
                </ChartCard>
              </Col>
              {parPriorite.length > 0 && evolHasData && (
                <Col xs={12} md={4}>
                  <ChartCard title="Par priorité">
                    <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <Doughnut
                        data={prioriteData}
                        options={{ ...noScales, plugins: { ...noScales.plugins, legend: { position: "bottom", labels: { color: textColor, boxWidth: 10, font: { size: 10 } } } } }}
                      />
                    </div>
                  </ChartCard>
                </Col>
              )}
            </Row>
          )}

          {/* ── Temps passé par intervenant ───────────────────────────── */}
          {intervParUser.length > 0 && (
            <Row className="mb-4 g-3">
              <Col xs={12}>
                <ChartCard title="Temps passé par intervenant" height={intervUserHeight}>
                  <Bar data={intervUserData} options={hBarDur} />
                </ChartCard>
              </Col>
            </Row>
          )}

          {/* ── Top tickets + top projets par durée ───────────────────── */}
          {(intervParTicket.length > 0 || projParProjet.length > 0) && (
            <Row className="mb-4 g-3">
              {intervParTicket.length > 0 && (
                <Col xs={12} md={projParProjet.length > 0 ? 8 : 12}>
                  <ChartCard title="Top tickets — temps passé" height={260}>
                    <Bar data={topTicketsData} options={hBarDur} />
                  </ChartCard>
                </Col>
              )}
              {projParProjet.length > 0 && (
                <Col xs={12} md={intervParTicket.length > 0 ? 4 : 12}>
                  <ChartCard title="Top projets — temps passé" height={260}>
                    <Bar data={topProjetsData} options={hBarDur} />
                  </ChartCard>
                </Col>
              )}
            </Row>
          )}

          {/* ── Page complète — par client (tickets + heures) ────────── */}
          {!compact && (tikParClient.length > 0 || intervParClient.length > 0) && (
            <Row className="mb-4 g-3">
              {tikParClient.length > 0 && (
                <Col xs={12} md={intervParClient.length > 0 ? 6 : 12}>
                  <ChartCard title="Tickets par client" height={280}>
                    <Bar data={tikParClientData} options={hBar} />
                  </ChartCard>
                </Col>
              )}
              {intervParClient.length > 0 && (
                <Col xs={12} md={tikParClient.length > 0 ? 6 : 12}>
                  <ChartCard title="Heures d'intervention par client" height={280}>
                    <Bar data={intervClientData} options={hBarDur} />
                  </ChartCard>
                </Col>
              )}
            </Row>
          )}

          {/* ── Page complète — tickets par utilisateur ───────────────── */}
          {!compact && tikParUser.length > 0 && (
            <Row className="mb-4 g-3">
              <Col xs={12}>
                <ChartCard title="Tickets par utilisateur" height={tikUserHeight}>
                  <Bar data={userTicketData} options={hBar} />
                </ChartCard>
              </Col>
            </Row>
          )}
        </>
      )}
    </div>
  );
};

export default StatsPanel;
